import {Component, computed, DestroyRef, inject, OnInit} from '@angular/core';
import {GlobalConstants} from "../../common/global";
import {Services} from "src/app/services/api.service"
import {environment} from './../../../environments/environment';
import {TranslateModule} from '@ngx-translate/core';
import {Router, RouterModule} from '@angular/router';
import {PagesService} from 'src/app/services/pages.service';
import {GetUserProfileService} from 'src/app/services/getUserProfile.service';
import {CommonModule} from '@angular/common';
import {LoginPopupComponent} from 'src/app/components/login-popup/login-popup.component';
import {ForgottenPasswordComponent} from 'src/app/components/forgotten-password/forgotten-password.component';
import {RegistrationComponent} from 'src/app/components/registration/registration.component';
import {NavigationCartComponent} from "../../components/navigation-cart/navigation-cart.component";
import {LanguageService} from 'src/app/services/language.service';
import {Language} from 'src/app/common/languages.config';
import {AppConfigService} from 'src/app/common/app-config/app-config.service';
import {AppConfig} from 'src/app/common/app-config/app-config.interface';
import {ModalService} from 'src/app/services/modal.service';
import {ModalNames} from 'src/app/common/modal.config';
import {BuyMembershipComponent} from 'src/app/pages/membership/buy-membership/buy-membership.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {concat, filter, skip} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {ProfileService} from 'src/app/services/profile.service';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';

interface NavItem {
  labelKey: string;
  route?: string;
  hiddenWhenLogged?: boolean;
  onlyWhenLogged?: boolean;
  external?: boolean;
  baseUrl?: string;
  downloadPath?: string;
}

@Component({
  selector: 'app-navigation-bar',
  templateUrl:  `./${environment.pageFolder}/navigation-bar.${environment.pageFolder}.component.html`,
  styleUrls:  [`./${environment.pageFolder}/navigation-bar.${environment.pageFolder}.component.sass`],
  standalone: true,
  imports: [RouterModule, TranslateModule, CommonModule, LoginPopupComponent, ForgottenPasswordComponent, RegistrationComponent, NavigationCartComponent, BuyMembershipComponent, MatToolbarModule, MatIconModule, MatButtonModule]
})

export class NavigationBarComponent implements OnInit {
  isLogged: boolean = false;
  homepageLink = environment.url;
  isNavOpen: boolean = false;
  username: string = "";
  redirectUrl = environment.redirectUrl;
  envPage = environment.page;
  activePage: any;
  gdprConsent!: boolean;
  hideNav: boolean = false;
  appConfig: AppConfig;
  page = environment.page;
  isLoginModalOpen = computed(() => this.modalService.modalsState().get(ModalNames.Login) ?? false);
  isForgottenPassModalOpen = computed(() => this.modalService.modalsState().get(ModalNames.ForgottenPassword) ?? false);
  isRegistrationModalOpen = computed(() => this.modalService.modalsState().get(ModalNames.Registration) ?? false);
  private readonly destroyRef = inject(DestroyRef);

  readonly currentLang = this.languageService.currentLanguage;
  public readonly languageEnum = Language;
  public readonly modalNamesEnum = ModalNames;

  navItems: NavItem[] = [
    { labelKey: 'NAVIGATION.HOMEPAGE', route: '/' },
    { labelKey: 'NAVIGATION.TICKETS', route: '/user-profile', onlyWhenLogged: true },
    {
      labelKey: 'NAVIGATION.TICKET_PRICES',
      route: '/prices'
    },
    { labelKey: 'NAVIGATION.ANNOUNCEMENTS', baseUrl: 'https://nkistra.com', external: true },
    { labelKey: 'NAVIGATION.LOGIN_REGISTER', hiddenWhenLogged: true },
    { labelKey: 'NAVIGATION.LOGOUT', onlyWhenLogged: true }
  ];

  constructor(
    public Services: Services,
    private readonly userProfileService: GetUserProfileService,
    public languageService: LanguageService,
    private router: Router,
    private pagesService: PagesService,
    private appConfigService: AppConfigService,
    public modalService: ModalService,
    private profileService: ProfileService
  ) {
    this.appConfig = this.appConfigService.getConfig();
  }

  ngOnInit(): void {
    let currentURL = window.location.href;
    this.activePage = currentURL.substring(currentURL.lastIndexOf('/') + 1);
    this.isLogged = GlobalConstants.isAuth;

    if (!this.isLogged) {
      return;
    }

    concat(
      this.userProfileService.getUserProfile(),
      this.userProfileService.userProfile$.pipe(skip(1))
    )
    .pipe(
      filter(userProfile => userProfile != null),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe({
      next: (userProfile) => {
        if (currentURL.includes('privacy-policy') && !userProfile.gdprCheck) {
          this.hideNav = true;
        }

        if (this.isLogged) {
          this.username = `${userProfile.firstName} ${userProfile.lastName}`;
        }
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.Services.logout();
        }
      }
    });
  }

  readonly visibleNavItems = computed(() => {
    const logged = this.isLogged;

    return this.navItems.filter((item) => {
      if (item.hiddenWhenLogged && logged) {
        return false;
      }
      return !(item.onlyWhenLogged && !logged);
    });
  });

  handleNavigation(item: NavItem): void {
    if (item.labelKey === 'NAVIGATION.LOGIN_REGISTER') {
      this.modalService.openModal(ModalNames.Login);

    } else if (item.labelKey === 'NAVIGATION.LOGOUT') {
      this.Services.logout();

    } else if (item.downloadPath) {
      return;

    } else if (item.external && item.baseUrl) {
      let path = '';
      switch (item.labelKey) {

        case 'NAVIGATION.ANNOUNCEMENTS':
          path = this.currentLang() === this.languageEnum.HR
            ? '/novosti/'
            : '/en/news/';
          break;
      }

      const url = item.baseUrl + path;
      window.open(url, '_blank', 'noopener');

    } else if (item.route) {
        void this.router.navigate([item.route]);
    }
  }

  toggleLanguage(): void {
    const next = this.currentLang() === Language.HR ? Language.EN : Language.HR;
    this.languageService.switchLanguage(next);
  }

  changeActivePage(activePage: string) {
    this.activePage = activePage;
    this.pagesService.changePage(activePage);
  }

  toggleNav() {
    this.isNavOpen = !this.isNavOpen;
  }

  changeUrl(url: string) {
    if(!this.isLogged) {
      this.modalService.openModal(ModalNames.Login);
      sessionStorage.setItem("openPage", url);
    } else {
      this.router.navigate([url]);
    }
  }

  changeRoute(url: string) {
    this.isNavOpen = this.isNavOpen ? false : true;
    this.router.navigate([url]);
  }
}
